import stringify from 'fast-safe-stringify'
import { Draft, produce } from 'immer'
import { StateCreator } from 'zustand'
import { devtools, persist, subscribeWithSelector } from 'zustand/middleware'
import { createWithEqualityFn } from 'zustand/traditional'

import { getPageKeyByPath } from '~/app/routes'
import { day, getOnlyDate, getToday } from '~/astra'
import { createUserSlice, UserSlice } from '~/store/slices/userSlice'

import { AppSlice, createAppSlice } from './slices/appSlice'
import { createDipsSlice, DipsSlice } from './slices/dipsSlice'
import { createDiSlice, DiSlice } from './slices/diSlice'
import { createFilterSlice, FilterSlice } from './slices/filterSlice'
import { createInsightsSlice, InsightsSlice } from './slices/insightsSlice'
import { createOccupanciesSlice, OccupanciesSlice } from './slices/occupanciesSlice'
import { createOperationalPlannerFilterSlice, OperationalPlannerFilterSlice } from './slices/operationalPlannerFilterSlice'
import { createOperationalPlannerSlice, OperationalPlannerSlice } from './slices/operationalPlannerSlice'
import { createOperationalPlannerViewSlice, OperationalPlannerViewSlice } from './slices/operationalPlannerViewOptionsSlice'
import { createSelectionDrawerSlice, SelectionDrawerSlice } from './slices/selectionDrawerSlice'
import { createSurgeonSchedulerFilterSlice, SurgeonSchedulerFilterSlice } from './slices/surgeonSchedulerFilterSlice'
import { createSurgeonSchedulerSlice, showLocationsModes, SurgeonSchedulerSlice } from './slices/surgeonSchedulerSlice'
import { createSurgeryBookmarksSlice, SurgeryBookmarksSlice } from './slices/surgeryBookmarksSlice'
import { createSurgerySchedulerFilterSlice, SurgerySchedulerFilterSlice } from './slices/surgerySchedulerFilterSlice'
import { createSurgerySchedulerSlice, SurgerySchedulerSlice } from './slices/surgerySchedulerSlice'
import { createSurgerySchedulerViewSlice, SurgerySchedulerViewSlice } from './slices/surgerySchedulerViewOptionsSlice'
import { createToasterSlice, ToasterSlice } from './slices/toasterSlice'
import { createWaitingListFilterSlice, WaitingListFilterSlice } from './slices/waitingListFilterSlice'
import { createWaitingListSlice, WaitingListSlice } from './slices/waitingListSlice'

export type AllSlices = DiSlice &
    AppSlice &
    FilterSlice &
    OccupanciesSlice &
    DipsSlice &
    UserSlice &
    OperationalPlannerSlice &
    OperationalPlannerFilterSlice &
    WaitingListFilterSlice &
    SurgerySchedulerFilterSlice &
    SurgeonSchedulerFilterSlice &
    OperationalPlannerViewSlice &
    WaitingListSlice &
    ToasterSlice &
    SurgeonSchedulerSlice &
    SurgerySchedulerSlice &
    SurgerySchedulerViewSlice &
    InsightsSlice &
    SelectionDrawerSlice &
    SurgeryBookmarksSlice

export type Slice<T> = StateCreator<AllSlices, [], [['zustand/immer', never]], T>

type PersistedState = {
    departmentKey: FilterSlice['appFilters']['departmentKey']
    isNavSidebarOpen: AppSlice['app']['isNavSidebarOpen']
    isPrivacyMode: AppSlice['app']['isPrivacyMode']
    selectedDate: string | null
    activeViews: AppSlice['app']['activeViews'] | Record<string, unknown>
    showLocations: SurgeonSchedulerSlice['surgeonScheduler']['showLocations']
    showLocationsMode: SurgeonSchedulerSlice['surgeonScheduler']['showLocationsMode']
    showSurgeonCounts: SurgeonSchedulerSlice['surgeonScheduler']['showSurgeonCounts']
    showTableFooters: SurgeonSchedulerSlice['surgeonScheduler']['showTableFooters']
    dateRange: SurgeonSchedulerSlice['surgeonScheduler']['dateRange']
    fullWidthCells: SurgeonSchedulerSlice['surgeonScheduler']['fullWidthCells']
    selectedSpecialties: FilterSlice['appFilters']['selectedSpecialties']
}

const PERSISTED_STORE_NAME = 'store'

function isPartializeStore(obj: unknown): obj is Partial<PersistedState> {
    if (typeof obj !== 'object' || obj === null) return false

    const persistedKeys = [
        'departmentKey',
        'isNavSidebarOpen',
        'selectedDate',
        'activeViews',
        'showLocations',
        // not all have to be present
    ] as const satisfies (keyof PersistedState)[]

    return persistedKeys.every(key => key in obj) || 'e2e' in obj
}

function restoreDate(date: string | null) {
    if (date === null) return getToday()

    const parsedDate = day(date)

    if (parsedDate.isValid()) return parsedDate

    return getToday()
}

const safeRestore = <T>(value: T | undefined, validValues: readonly T[], defaultValue: T) => {
    if (!value) return defaultValue
    return validValues.includes(value) ? value : defaultValue
}

export function clearPartialStorage() {
    const currentStorageState = JSON.parse(localStorage.getItem(PERSISTED_STORE_NAME) || '{}')
    const isPersistedState = typeof currentStorageState === 'object' && currentStorageState !== null && 'state' in currentStorageState

    if (isPersistedState && isPartializeStore(currentStorageState.state)) {
        localStorage.setItem(
            PERSISTED_STORE_NAME,
            stringify({
                state: {
                    ...currentStorageState.state,
                    filters: { all: [] },
                    selectedDate: null,
                    isNavSidebarOpen: true,
                    activeViews: {},
                },
            })
        )
    }
}

export const useStore = createWithEqualityFn<AllSlices>()(
    subscribeWithSelector(
        devtools(
            persist(
                (...args) => ({
                    ...createAppSlice(...args),
                    ...createDiSlice(...args),
                    ...createFilterSlice(...args),
                    ...createDipsSlice(...args),
                    ...createUserSlice(...args),
                    ...createOperationalPlannerSlice(...args),
                    ...createOperationalPlannerFilterSlice(...args),
                    ...createOperationalPlannerViewSlice(...args),
                    ...createWaitingListFilterSlice(...args),
                    ...createSurgerySchedulerFilterSlice(...args),
                    ...createSurgeonSchedulerFilterSlice(...args),
                    ...createWaitingListSlice(...args),
                    ...createToasterSlice(...args),
                    ...createSurgeonSchedulerSlice(...args),
                    ...createSurgerySchedulerSlice(...args),
                    ...createSurgerySchedulerViewSlice(...args),
                    ...createOccupanciesSlice(...args),
                    ...createInsightsSlice(...args),
                    ...createSelectionDrawerSlice(...args),
                    ...createSurgeryBookmarksSlice(...args),
                }),
                {
                    name: PERSISTED_STORE_NAME,
                    partialize: (state): PersistedState => ({
                        departmentKey: state.appFilters.departmentKey,
                        selectedSpecialties: state.appFilters.selectedSpecialties,
                        isNavSidebarOpen: state.app.isNavSidebarOpen,
                        isPrivacyMode: state.app.isPrivacyMode,
                        selectedDate: getOnlyDate(state.appFilters.selectedDate),
                        activeViews: state.app.activeViews,
                        showLocations: state.surgeonScheduler.showLocations,
                        showLocationsMode: state.surgeonScheduler.showLocationsMode,
                        showSurgeonCounts: state.surgeonScheduler.showSurgeonCounts,
                        showTableFooters: state.surgeonScheduler.showTableFooters,
                        dateRange: state.surgeonScheduler.dateRange,
                        fullWidthCells: state.surgeonScheduler.fullWidthCells,
                    }),
                    merge: (persistedState, initialState) =>
                        produce(initialState, (draft: Draft<AllSlices>) => {
                            if (isPartializeStore(persistedState)) {
                                draft.appFilters.departmentKey = persistedState.departmentKey ?? initialState.appFilters.departmentKey
                                // Empty signals "unresolved"; useResolveSelectedSpecialties derives it from the department once DI entities load.
                                draft.appFilters.selectedSpecialties = persistedState.selectedSpecialties ?? []
                                draft.app.isNavSidebarOpen = persistedState.isNavSidebarOpen ?? initialState.app.isNavSidebarOpen
                                draft.app.isPrivacyMode = persistedState.isPrivacyMode ?? initialState.app.isPrivacyMode
                                draft.appFilters.selectedDate = restoreDate(persistedState.selectedDate ?? null)
                                draft.surgeonScheduler.showLocations = persistedState.showLocations ?? initialState.surgeonScheduler.showLocations
                                draft.surgeonScheduler.showLocationsMode = safeRestore(
                                    persistedState.showLocationsMode,
                                    showLocationsModes,
                                    initialState.surgeonScheduler.showLocationsMode
                                )
                                draft.surgeonScheduler.showSurgeonCounts = persistedState.showSurgeonCounts ?? initialState.surgeonScheduler.showSurgeonCounts
                                draft.surgeonScheduler.showTableFooters = persistedState.showTableFooters ?? initialState.surgeonScheduler.showTableFooters
                                draft.surgeonScheduler.dateRange = persistedState.dateRange ?? initialState.surgeonScheduler.dateRange
                                draft.surgeonScheduler.fullWidthCells = persistedState.fullWidthCells ?? initialState.surgeonScheduler.fullWidthCells

                                const pathname = window.location.pathname
                                const pageKey = getPageKeyByPath(pathname)

                                if (!pageKey) {
                                    draft.app.activeViews = { ...initialState.app.activeViews, ...persistedState.activeViews }
                                } else {
                                    draft.app.activeViews = {
                                        ...initialState.app.activeViews,
                                        ...persistedState.activeViews,
                                        [pageKey]: pathname,
                                    }
                                }
                            }
                        }),
                }
            )
        )
    )
)

export type StoreState = ReturnType<typeof useStore.getState>

// Exporting these for backwards compatibility for now
export * from './slices/diSlice'
